import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { NotificationTypes } from 'src/app/app.enums';
import { copyToClipboard } from 'src/app/app.functions';
import { ArtFlowSelectItem } from 'src/app/models/artflow.selectitem';
import { EmailEventTypeService } from 'src/app/Services/email-event-type.service';
import { Events, Parameters, SaveTemplates } from 'src/app/models/emailevents.model';
import { EmailEventsService, EmailParameterResponse, SendEmailEventData } from 'src/app/Services/email-events.service';
import { AngularEditorComponent } from '@kolkov/angular-editor';
import { SmsTemplatePayload, SmsTemplateService } from 'src/app/Services/sms-template-service/sms-template.service';
import { EmailEventsFilters } from 'src/app/Services/auth.service';
import { forkJoin, of } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'sms-settings',
    templateUrl: './sms-settings.component.html',
    styleUrls: ['./sms-settings.component.css'],
    providers: [SmsTemplateService],
    standalone: false
})
export class SMSSettingsComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  @ViewChild('emailEditor') emailEditor!: AngularEditorComponent;
  public emailEvents: Events[] = [];
  public selectedEmailEvent?: ArtFlowSelectItem;
  public selectedSmsTemplate: SaveTemplates = new SaveTemplates();
  public isPredefined: boolean = false;
  public emailParameters: Parameters[] = [];
  public testEmailAddress: string = '';
  public selectedEmailParameter: string | null = null;
  public selectedEmailParameterId!: string;
  public EmailParameterData: EmailParameterResponse = {} as EmailParameterResponse;
  public isEditorInVisualMode: boolean = true;
  public displayDialog: boolean = false;
  public emailPreviewVisible: boolean = false;
  public isAddEmailEventDailogVisible: boolean = false;
  public isEditMode: boolean = false;
  public allemailEvents: SmsTemplatePayload[] = [];
  public emailEventsFormData: Events = new Events();
  private pasteListenerAdded = false;

  constructor(
    private confirmationService: ConfirmationService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private emailEventsService: EmailEventsService,
    private smsTemplateService: SmsTemplateService,
    private emailEventTypeService: EmailEventTypeService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.getAllSmsTemplates();
  }

  ngOnDestroy(): void { }

  onEmailEventChanged($event: any) {
    this.getEmailTemplateParameters($event.value);
  }

  onEmailEditorValueChanged(event: any): void {
    this.selectedSmsTemplate.HtmlText = event.value;
  }

  onEmailParameterChanged($event: any) {
    this.selectedEmailParameterId = $event.value;
    const emailparameter = this.emailParameters.find((p) => p.Guid === $event.value);
    this.EmailParameterData.ParameterUsage = emailparameter?.ParameterUsage || '';
    this.EmailParameterData.ParameterDescription = emailparameter?.ParameterDescription || '';
  }

  saveEmailParameterField($event: any) {
    this.EmailParameterData.Guid = this.selectedEmailParameterId;
    this.ngxSpinnerService.show();
    this.emailEventTypeService.saveEmailParameterData(this.EmailParameterData).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.notificationsService.showNotification(this.transloco.translate('smsSettings.success'), response.ResponseMessage, NotificationTypes.Success);
        } else {
          this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  focusEditor($event: any) {
    $event.preventDefault();
    if (!this.pasteListenerAdded && window.addEventListener) {
      window.addEventListener('paste', ($event: any) => {
        $event.preventDefault();
        try {
          const text = $event.clipboardData.getData("text/plain");
          if (text) {
            const editableElement = this.isEditorInVisualMode
              ? document.getElementsByClassName('angular-editor-textarea')[0] as HTMLElement
              : document.getElementById('sourceText');
            if (editableElement) {
              const range = document.createRange();
              const textNode = document.createTextNode(text);
              editableElement.innerHTML = '';
              editableElement.appendChild(textNode);
              range.selectNodeContents(textNode);
              editableElement.appendChild(range.createContextualFragment(text));
            }
          }
        } catch (e) {
          console.error(e);
        }
      });
      this.pasteListenerAdded = true;
    }
  }

  onViewModeChanged($event: boolean) {
    this.isEditorInVisualMode = $event;
  }

  copySelectedEmailParameter($event: any) {
    const selectedParameter = this.emailParameters.find(param => param.Guid === this.selectedEmailParameter);
    if (selectedParameter?.ParameterName) {
      copyToClipboard(`{{${selectedParameter.ParameterName}}}`);
      this.notificationsService.showNotification(this.transloco.translate('smsSettings.success'), this.transloco.translate('smsSettings.parameterCopied'), NotificationTypes.Success);
    }
  }

  insertEmailParameter($event: any) {
    const editorElement = this.emailEditor.textArea.nativeElement;
    const cursorPosition = this.getCursorPosition(editorElement);
    if (cursorPosition > -1) {
      const selectedParameter = this.emailParameters.find(param => param.Guid === this.selectedEmailParameter);
      this.insertTextAtCursorPosition(cursorPosition, selectedParameter?.ParameterName);
    }
  }

  private getCursorPosition(editorElement: HTMLTextAreaElement): number {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preSelectionRange = range.cloneRange();
      preSelectionRange.selectNodeContents(editorElement);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      return preSelectionRange.toString().length + range.toString().length;
    }
    return -1;
  }

  private insertTextAtCursorPosition(cursorPosition: number, parameterText: string | undefined): void {
    const currentValue = this.selectedSmsTemplate.HtmlText?.replace(/&nbsp;/g, ' ') || '';
    const newValue = currentValue.substring(0, cursorPosition) + `{{${parameterText}}}` + currentValue.substring(cursorPosition);
    this.selectedSmsTemplate.HtmlText = newValue.replace(/&nbsp;/g, ' ');
    this.selectedEmailParameter = null;
  }

  getAllSmsTemplates() {
    this.loading = true;
    this.ngxSpinnerService.show();
    this.smsTemplateService.getAllSmsTemplates().subscribe(
      (allemailEvents: SmsTemplatePayload[]) => {
        this.allemailEvents = allemailEvents;
        this.ngxSpinnerService.hide();
        this.loading = false;
      },
      (error) => {
        console.error('Error fetching SMS templates:', error);
        this.ngxSpinnerService.hide();
        this.loading = false;
      }
    );
  }

  private getEmailTemplateParameters(eventId: string) {
    this.emailEventTypeService.fetchEmailParameters(eventId).subscribe({
      next: (emailEvents: Parameters[]) => {
        this.emailParameters = emailEvents;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  saveEmailTemplate($event: any) {
    if (this.selectedSmsTemplate.HtmlText) {
      this.selectedSmsTemplate.HtmlText = this.selectedSmsTemplate.HtmlText.replace(/&nbsp;/g, ' ');
    }
    const smsTemplatePayload: SmsTemplatePayload = {
      id: this.selectedSmsTemplate.Guid || '',
      systemEventId: this.selectedSmsTemplate.EmailEventId,
      text: this.selectedSmsTemplate.HtmlText || '',
      createdDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      eventTypeViewModel: {
        id: this.selectedSmsTemplate.EmailEventId,
        name: this.emailEvents.find(e => e.Id === this.selectedSmsTemplate.EmailEventId)?.Name || '',
        description: this.emailEvents.find(e => e.Id === this.selectedSmsTemplate.EmailEventId)?.Description || '',
        isPredefined: this.isEditMode ? this.isPredefined : false,
        isEmailEvent: false,
        createdDate: new Date().toISOString(),
        emailParameterIds: []
      }
    };
    this.ngxSpinnerService.show();
    this.smsTemplateService.saveSmsTemplate(smsTemplatePayload).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.notificationsService.showNotification(this.transloco.translate('smsSettings.success'), response.ResponseMessage, NotificationTypes.Success);
          this.onCloseCreateDialog();
          this.getAllSmsTemplates();
        } else {
          this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  sendTestEmail($event: any) {
    const testData: SendEmailEventData = {
      SendTo: this.testEmailAddress,
      Subject: this.selectedSmsTemplate.Subject,
      HtmlContent: this.selectedSmsTemplate.HtmlText
    };
    this.emailEventsService.sendTestEmail(testData).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.testEmailAddress = '';
          this.notificationsService.showNotification(this.transloco.translate('smsSettings.success'), response.ResponseMessage, NotificationTypes.Success);
        } else {
          this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  private getIdFromPayload(payload: any): string | undefined {
    return payload?.id ?? payload?.Id ?? payload?.Guid ?? payload?.GUID;
  }

  showEditDialog(event: any) {
    const id = this.getIdFromPayload(event);
    const systemEventId = event?.systemEventId ?? event?.SystemEventId;
    const text = event?.text ?? event?.Text;
    const evtVM = event?.eventTypeViewModel ?? event?.EventTypeViewModel;

    this.ngxSpinnerService.show();

    // Load both email events and template data in parallel
    forkJoin({
      emailEvents: this.emailEventTypeService.fetchEmailevents(EmailEventsFilters.All),
      smsTemplate: id ? this.smsTemplateService.getSmsTemplate(id) : of(null)
    }).subscribe({
      next: (results) => {
        // Process email events
        this.emailEvents = results.emailEvents.map(eventItem => ({
          Id: eventItem.Id,
          Name: eventItem.Name,
          Description: eventItem.Description,
          IsPredefined: eventItem.IsPredefined,
          CreatedDate: eventItem.CreatedDate,
          EmailParameterIds: eventItem.EmailParameterIds,
          label: eventItem.IsPredefined ? `${eventItem.Name} (system)` : eventItem.Name,
          value: eventItem.Id,
        }));

        // Use existing data if available, otherwise use API response
        const templateData = results.smsTemplate || event;

        const selectedEvent = this.emailEvents.find(e => e.Id === systemEventId);
        this.selectedEmailEvent = {
          label: selectedEvent?.Name ?? evtVM?.name ?? evtVM?.Name ?? '',
          value: systemEventId,
          data: selectedEvent?.Description ?? evtVM?.description ?? evtVM?.Description ?? ''
        } as ArtFlowSelectItem;

        this.selectedSmsTemplate = {
          Guid: id || '',
          HtmlText: text || templateData?.text,
          EmailEventId: systemEventId || templateData?.systemEventId,
          Subject: evtVM?.name ?? evtVM?.Name ?? templateData?.eventTypeViewModel?.name
        };

        // Load parameters only if we have an event ID
        if (systemEventId || templateData?.systemEventId) {
          this.getEmailTemplateParameters(systemEventId || templateData.systemEventId);
        }

        this.isEditMode = true;
        this.displayDialog = true;
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), this.transloco.translate('smsSettings.failedToLoadTemplate'), NotificationTypes.Error);
      }
    });
  }

  private getSmsTemplate(payloadOrId: { id: string } | string | null) {
    const id = typeof payloadOrId === 'string' ? payloadOrId : payloadOrId?.id;
    if (!id) {
      console.error("No ID provided for SMS Template:", payloadOrId);
      return;
    }

    this.ngxSpinnerService.show();
    this.smsTemplateService.getSmsTemplate(id).subscribe({
      next: (smsTemplate) => {
        if (smsTemplate) {
          this.selectedSmsTemplate = {
            Guid: smsTemplate.id,
            HtmlText: smsTemplate.text,
            EmailEventId: smsTemplate.systemEventId,
            Subject: smsTemplate.eventTypeViewModel?.name
          };
        }
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading template:', error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), this.transloco.translate('smsSettings.failedToLoadTemplate'), NotificationTypes.Error);
      }
    });
  }

  fetchEmailEvents() {
    this.ngxSpinnerService.show();
    this.emailEventTypeService.fetchEmailevents(EmailEventsFilters.All).subscribe({
      next: (emailEvents: Events[]) => {
        this.emailEvents = emailEvents.map(event => ({
          Id: event.Id,
          Name: event.Name,
          Description: event.Description,
          IsPredefined: event.IsPredefined,
          CreatedDate: event.CreatedDate,
          EmailParameterIds: event.EmailParameterIds,
          label: event.Name,
          value: event.Id
        }));
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    });
  }

  getEmailParameters() {
    this.emailEventTypeService.fetchEmailParameters().subscribe(
      (emailParameters) => {
        this.emailParameters = emailParameters;
      },
      (error) => {
        console.error(error);
      }
    );
  }
  openDialog() {
    // Reset all state before opening dialog for new template
    this.selectedSmsTemplate = new SaveTemplates();
    this.selectedEmailEvent = undefined;
    this.emailParameters = [];
    this.isEditMode = false;

    this.displayDialog = true;
    this.fetchEmailEvents();
  }

  onAddEmailEvents() {
    this.isAddEmailEventDailogVisible = true;
    this.getEmailParameters();
  }

  onCloseEmailEventsDialog($event: boolean) {
    this.emailEventsFormData = new Events();
    this.isAddEmailEventDailogVisible = $event;
  }

  onCloseCreateDialog() {
    this.displayDialog = false;
    this.selectedSmsTemplate = new SaveTemplates();
  }

  onCloseEmailTemplateDialog($event: boolean) {
    this.displayDialog = $event;
    // Reset the state when dialog is closed
    if (!$event) {
      this.selectedSmsTemplate = new SaveTemplates();
      this.selectedEmailEvent = undefined;
      this.emailParameters = [];
      this.isEditMode = false;
    }
  }

  onDeleteEmailTemplate(event: any) {
    if (!event?.Id) {
      console.error('Invalid or missing template ID:', event?.Id);
      this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), this.transloco.translate('smsSettings.cannotDeleteInvalidId'), NotificationTypes.Error);
      return;
    }

    this.confirmationService.confirm({
      message: this.transloco.translate('smsSettings.confirmDeleteTemplate', { name: event.EventTypeViewModel.Name || 'this' }),
      accept: () => {
        this.ngxSpinnerService.show();
        this.smsTemplateService.deleteSmsTemplate(event.Id).subscribe({
          next: (response) => {
            this.ngxSpinnerService.hide();
            if (response.Success) {
              this.notificationsService.showNotification(this.transloco.translate('smsSettings.success'), response.ResponseMessage, NotificationTypes.Success);
              this.getAllSmsTemplates();
            } else {
              this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), response.ResponseMessage, NotificationTypes.Error);
            }
          },
          error: (error) => {
            console.error('Error deleting template:', error);
            this.ngxSpinnerService.hide();
            this.notificationsService.showNotification(this.transloco.translate('smsSettings.error'), this.transloco.translate('smsSettings.failedToDeleteTemplate'), NotificationTypes.Error);
          }
        });
      }
    });
  }

  showEmailLogDailog() {
    this.emailPreviewVisible = true;
  }
}